/**
 * The month under way.
 *
 * Every other figure on this screen is retrospective — it prices a breach that already
 * happened, and a credit does not buy back the weeks it compensates for. This panel is
 * the one place the platform says something while the outcome can still change.
 *
 * Each row carries its own working: what has been seen, how much of the month has run,
 * and what that projects to. A flag that cannot answer "on what basis" is not something
 * anyone will take to a supplier.
 */

import { Notice, Section } from "./Chrome";
import { periodLabel } from "../lib/format";
import type { AtRisk } from "../lib/types";

const METHOD_LABEL: Record<string, string> = {
  linear_run_rate: "the rate observed so far, carried forward",
  extrapolated_total: "the count so far, scaled to a full month",
};

export function AtRiskPanel({ items, openPeriod }: { items: AtRisk[]; openPeriod: string }) {
  if (items.length === 0) return null;
  const elapsed = Math.round((items[0]?.as_of_fraction ?? 0) * 100);

  return (
    <Section
      title={`Heading for a breach — ${periodLabel(openPeriod)}`}
      subtitle={
        `${elapsed}% of the month has run. These are not breaches yet, and that is the ` +
        `point: there is still time to do something about them.`
      }
    >
      <div className="card table-scroll">
        <table>
          <thead>
            <tr>
              <th>Service level</th>
              <th>Target</th>
              <th style={{ textAlign: "right" }}>Projected at close</th>
              <th>On what basis</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.service_level_id}>
                <td className="serif" style={{ fontSize: 14.5 }}>
                  {item.service_level_name}
                </td>
                <td style={{ color: "var(--slate)", whiteSpace: "nowrap" }}>
                  ≥ {item.expected_target}
                  {item.unit}
                </td>
                <td
                  className="num serif"
                  style={{ textAlign: "right", color: "var(--rust)", fontSize: 16 }}
                >
                  {item.projected_value}
                  {item.unit}
                </td>
                {/* The observed figure lives here rather than in a column of its own: on
                    a rate metric the run rate is the projection, so two columns would
                    show the same number twice and read as a fault. */}
                <td style={{ fontSize: 12, color: "var(--slate)", maxWidth: 340 }}>
                  {item.sample_size} events over {Math.round(item.as_of_fraction * 100)}% of
                  the month, currently {item.observed_value}
                  {item.unit}, projected on {METHOD_LABEL[item.method] ?? item.method}.
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 12 }}>
        <Notice>
          A projection assumes the rest of the month behaves like the part already seen.
          That is the assumption, stated plainly, and it is the one a supplier will
          challenge first — which is why the sample size and the elapsed fraction sit
          beside every figure rather than behind them.
        </Notice>
      </div>
    </Section>
  );
}
